import { Injectable } from '@angular/core';
import { lastValueFrom, take } from 'rxjs';

import { ApplicationStateService } from '@core/services/application-state.service';
import { FlowType } from '@core/enums/flow-type.enum';
import {
  AdministrationDeviceRoutePaths,
  AdministrationRoutePaths,
  AppRoutePaths,
  PageKeyRoutes,
} from '@core/constants';
import { PageKey } from '@core/enums/page-keys.enum';
import { WorkflowEngineService } from '@core/workflow-engine/workflow-engine.service';
import { checkinProcessFlow } from '@core/workflow-engine/flows/checkin-process.flow';
import { DeviceConfigurationsService } from '@data/services/devices-configuration.service';
import { TimerService } from './timer.service';
import type {
  CheckinWorkflow,
  WorkflowVariables,
} from '@core/workflow-engine/workflow-engine.types';

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private readonly workflowEngine = new WorkflowEngineService();

  constructor(
    private applicationStateService: ApplicationStateService,
    private deviceConfigurationsService: DeviceConfigurationsService,
    private timerService: TimerService,
  ) {}

  get hasActiveWorkflow(): boolean {
    return this.workflowEngine.hasActiveWorkflow;
  }

  async initializeSession(): Promise<string> {
    this.applicationStateService.resetSession();

    const deviceId = await lastValueFrom(this.applicationStateService.deviceId$.pipe(take(1)));
    if (!deviceId) {
      return AppRoutePaths.Error;
    }

    const deviceConfiguration = await this.deviceConfigurationsService.getByDeviceId(deviceId);
    await this.applicationStateService.setDeviceConfiguration(deviceConfiguration);

    if (!deviceConfiguration.geminiSiteId) {
      return `${AppRoutePaths.Administration}/${AdministrationRoutePaths.Device}/${AdministrationDeviceRoutePaths.General}`;
    }

    const page = await this.startEngine(checkinProcessFlow);
    this.timerService.reset();

    return this.getRoutePath(page ?? PageKey.CLOSING_SESSION) ?? AppRoutePaths.NotFound;
  }

  async next(): Promise<PageKey | null> {
    return this.workflowEngine.next(await this.buildVariables());
  }

  async resume(): Promise<PageKey | null> {
    return this.workflowEngine.resume(await this.buildVariables());
  }

  async back(): Promise<PageKey | null> {
    return this.workflowEngine.back(await this.buildVariables());
  }

  async bypass(): Promise<PageKey | null> {
    return this.workflowEngine.bypass(await this.buildVariables());
  }

  async exit(): Promise<PageKey | null> {
    return this.workflowEngine.exit(await this.buildVariables());
  }

  async buildVariables(): Promise<WorkflowVariables> {
    const state = await lastValueFrom(this.applicationStateService.applicationState$.pipe(take(1)));
    const flowInformation = state.flowInformation;
    const visit = state.visit;
    const checkinProcessConfiguration =
      state.technicalInformation?.trainingCenterConfiguration?.checkinProcessConfiguration;
    const training = flowInformation.training;

    return {
      flowType: flowInformation.flowType,
      visitorType: visit.visitorType,
      traineeType: visit.traineeType,
      recordingType: flowInformation.recordingType,
      identityDocumentType: flowInformation.identityDocumentType,
      checkinProcessConfiguration,
      hasConsentedIdentityVerification: visit.identityPolicyConsentDate != null,
      hasAcknowledgeCovidVerification: flowInformation.covidAcknowledgementDate != null,
      hasExtraInformationPage: flowInformation.hasExtraInformationPage ?? false,
      isScanPassportSuccess: flowInformation.isScanPassportSuccess ?? false,
      isScanGovernmentIdSuccess: flowInformation.isScanGovernmentIdSuccess ?? false,
      isScanDocumentSuccess: flowInformation.isScanDocumentSuccess ?? false,
      isIdentityComparaisonSuccess: flowInformation.isIdentityComparaisonSuccess ?? false,
      isCheckinSuccess: flowInformation.isCheckinSuccess ?? false,
      isIdPictureSuccess: flowInformation.isIdPictureSuccess ?? false,
      isSelfiePictureSuccess: flowInformation.isSelfiePictureSuccess ?? false,
      isPrintBadgeSuccess: flowInformation.isPrintBadgeSuccess ?? false,
      isScanQrCodeValidationSuccess: flowInformation.isScanQrCodeValidationSuccess ?? false,
      trainingId: training?.id ?? visit.trainingId,
      visitStatus: visit.status ?? flowInformation.badgeValidity?.checkinStatus,
      tsaStatus: training?.tsaStatus ?? visit.tsaStatus,
      isVisitPrecheckedIn:
        flowInformation.flowType === FlowType.EXISTING_QRCODE &&
        visit.isVisitPrecheckedIn(checkinProcessConfiguration?.hasCAEConnectFlow ?? false),
      isEmailProvided: Boolean(visit.email),
      skipPrintBadge: state.technicalInformation?.skipBadgePrinting ?? false,
      exportControl: visit.exportControl,
    };
  }

  getRoutePath(pageKey: PageKey): string | null {
    return PageKeyRoutes[pageKey] ?? null;
  }

  private async startEngine<TStep extends string>(
    rootWorkflow: CheckinWorkflow<TStep>,
  ): Promise<PageKey | null> {
    return this.workflowEngine.start(rootWorkflow, await this.buildVariables());
  }
}
